import axios, { AxiosProgressEvent } from 'axios';
import { executeApiCall } from 'services/api';
import { getMutations } from 'entities/hooks/mutation';
import { downloadFromURL } from 'utils/download';

import { OrString } from 'types';

export type UploadPrefix = OrString<
  'users' | 'packages' | 'invoiceId' | 'projects' | 'projectId' | 'feedback'
>;

interface BaseURLConfig {
  prefix?: UploadPrefix;
  enforcedName?: string;
  type?: 'PUBLIC' | 'PRIVATE';
}

interface UploadURLGenerationParams extends BaseURLConfig {
  contentType: string;
  fileName: string;
}

export interface ExecuteUploadParams extends BaseURLConfig {
  file: File;
  base64?: string | null;
  onUploadProgress?: (e: AxiosProgressEvent) => void;
}

interface MultiFileUploadParams extends Omit<ExecuteUploadParams, 'file' | 'base64'> {
  files: File[];
}

interface UploadUrlResult {
  uploadURL: string;
  url: string;
}

interface DownloadFileParams {
  url: string;
  onProgress?: (progress: number) => void;
  name: string;
}

function convertBase64ToBlob(file: File, base64?: string | null): Blob | undefined {
  if (!base64) return;

  const [, imageData] = base64.split(',');

  const binaryArray = new Uint8Array(
    Array.from(window.atob(imageData), (char) => char.charCodeAt(0)),
  );

  const blob = new Blob([binaryArray], { type: file.type });

  return blob;
}

const generateURL = async (payload: UploadURLGenerationParams): Promise<UploadUrlResult> => {
  return executeApiCall({
    path: '/uploads/signed-url',

    payload,
  });
};

const fileUpload = async ({
  file,
  base64,
  onUploadProgress,
  ...config
}: ExecuteUploadParams): Promise<{ url: string }> => {
  const blob = convertBase64ToBlob(file, base64);

  const { uploadURL, url } = await generateURL({
    contentType: file.type,
    fileName: file.name,
    ...config,
  });

  await axios.put(uploadURL, blob ?? file, {
    onUploadProgress,
    headers: {
      'Content-Type': file.type,
    },
  });

  return { url };
};

export const uploadMutations = getMutations({
  generateUploadURL: {
    action: generateURL,
  },

  uploadFile: {
    action: fileUpload,
  },

  multiFileUpload: {
    action: async ({ files, ...config }: MultiFileUploadParams) => {
      const uploaded = await Promise.all(
        files.map(async (file) => {
          const { url } = await fileUpload({
            file,
            ...config,
          });

          return {
            url,
            type: file.type || '',
            name: file.name || 'file',
          };
        }),
      );

      return {
        files: uploaded,
      };
    },
  },

  downloadFile: {
    action: async ({ url, onProgress, name }: DownloadFileParams): Promise<{ url: string }> => {
      const { accessUrl } = await executeApiCall<{ accessUrl: string }>({
        path: '/uploads/access-url',

        payload: { url },
      });

      await downloadFromURL({
        url: accessUrl,
        name,
        onProgress,
      });

      return { url };
    },
  },

  getPublicUrl: {
    action: async ({ url }: { url: string }): Promise<{ url: string }> => {
      const { accessUrl } = await executeApiCall<{ accessUrl: string }>({
        path: '/uploads/access-url',

        payload: { url },
      });

      return { url: accessUrl };
    },
  },
});
